import { useState, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Sparkles, Loader2, AlertCircle, Send, Info, X } from 'lucide-react';
import { TokenInfo, ClauseInfo, NounChunkInfo, SentenceInfo } from '../../types';
import { isProcessToken, isParticipantToken } from '../../utils/groupingRules';
import { evaluateAllTranslations } from '../../services/ai';
import { useSoundHandlers } from '../../hooks/useSoundEffects';

interface Step5Props {
  tokens: TokenInfo[];
  clauses: ClauseInfo[];
  nounChunks: NounChunkInfo[];
  sentences: SentenceInfo[];
  onComplete: (score: number, aiFeedback?: string) => void;
  isCompleted: boolean;
  showProcessHint?: boolean;
  showParticipantHint?: boolean;
  aiConfigured?: boolean;
}

// 小句片段结构（与 Step4Clause 一致）
interface ClauseSegment {
  id: string;
  text: string;
  tokens: TokenInfo[];
  level: number;
  clauseInfo?: ClauseTypeInfo;
}

// 从句类型信息
interface ClauseTypeInfo {
  type: string;
  description: string;
  headWord?: string;
}

// 单层评分结果
interface LayerScore {
  level: number;
  score: number;
  feedback: string;
  reference: string;
}

// 完整评估结果（每层 + 综合）
interface FullEvaluation {
  layerScores: LayerScore[];
  overall: {
    score: number;
    feedback: string;
    reference: string;
    improvements: string[];
  };
}

// 每个层级的宽度
const LEVEL_WIDTH = 48;

// 分析从句类型
function analyzeClauseType(
  levelTokens: TokenInfo[], 
  _allTokens: TokenInfo[], 
  _clauses: ClauseInfo[],
  level: number
): ClauseTypeInfo | undefined {
  if (level === 0) {
    return {
      type: '主句',
      description: '句子的主干部分，包含核心的主语和谓语',
    };
  }

  const hasSubSx = levelTokens.some(t => t.in_sub_sx);
  const hasSubCs = levelTokens.some(t => t.in_sub_cs);
  const hasSubDx = levelTokens.some(t => t.in_sub_dx);

  let headWord: string | undefined;
  for (const token of levelTokens) {
    const headToken = _allTokens.find((t: TokenInfo) => t.index_doc === token.head_i && t.cls_level < level);
    if (headToken) {
      headWord = headToken.text;
      break;
    }
  }

  let type: string;
  let description: string;

  if (hasSubSx) {
    type = '定语从句';
    description = headWord 
      ? `修饰名词 "${headWord}"，对其进行补充说明` 
      : '修饰前面的名词，对其进行补充说明';
  } else if (hasSubCs) {
    type = '状语从句';
    description = headWord 
      ? `修饰动词 "${headWord}"，表示时间、原因、条件等` 
      : '修饰谓语动词，表示时间、原因、条件等';
  } else if (hasSubDx) {
    type = '宾语从句';
    description = headWord 
      ? `作动词 "${headWord}" 的宾语，表示动作的内容` 
      : '作动词的宾语，表示动作的内容';
  } else {
    type = '从句';
    description = headWord 
      ? `与 "${headWord}" 相关的嵌套结构` 
      : '嵌套的从句结构';
  }

  return { type, description, headWord };
}

// 将 tokens 按 cls_level 变化分段（与 Step4Clause 一致）
function segmentByClauses(tokens: TokenInfo[], allTokens: TokenInfo[], clauses: ClauseInfo[]): ClauseSegment[] {
  const segments: ClauseSegment[] = [];
  let currentSegment: TokenInfo[] = [];
  let currentLevel: number | null = null;
  let segmentId = 0;
  
  tokens.forEach((token) => {
    if (token.pos === 'SPACE') return;
    
    if (token.pos === 'PUNCT') {
      if (currentSegment.length > 0) {
        currentSegment.push(token);
      }
      return;
    }
    
    if (currentLevel === null) {
      currentLevel = token.cls_level;
      currentSegment.push(token);
    } else if (token.cls_level === currentLevel) {
      currentSegment.push(token);
    } else {
      if (currentSegment.length > 0) {
        const clauseInfo = analyzeClauseType(currentSegment, allTokens, clauses, currentLevel);
        segments.push({
          id: `segment-${segmentId++}`,
          text: currentSegment.map(t => t.text).join(' ').replace(/ ([,.])/g, '$1'),
          tokens: currentSegment,
          level: currentLevel,
          clauseInfo,
        });
      }
      currentSegment = [token];
      currentLevel = token.cls_level;
    }
  });
  
  if (currentSegment.length > 0 && currentLevel !== null) {
    const clauseInfo = analyzeClauseType(currentSegment, allTokens, clauses, currentLevel);
    segments.push({
      id: `segment-${segmentId}`,
      text: currentSegment.map(t => t.text).join(' ').replace(/ ([,.])/g, '$1'),
      tokens: currentSegment,
      level: currentLevel,
      clauseInfo,
    });
  }
  
  return segments;
}

export default function Step5Translation({ 
  tokens, 
  clauses,
  onComplete, 
  showProcessHint = false,
  showParticipantHint = false,
  aiConfigured = false,
}: Step5Props) {
  const { playClick } = useSoundHandlers();
  const [translations, setTranslations] = useState<Map<string, string>>(new Map());
  const [submittedSegments, setSubmittedSegments] = useState<Set<string>>(new Set());
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluation, setEvaluation] = useState<FullEvaluation | null>(null);
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [showClauseInfo, setShowClauseInfo] = useState<string | null>(null);
  
  // 输入框引用，用于自动聚焦
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  // 使用与 Step4Clause 一致的分段逻辑
  const segments = useMemo(() => segmentByClauses(tokens, tokens, clauses), [tokens, clauses]);

  // 计算最大层级用于刻度线
  const maxLevel = useMemo(() => 
    Math.max(...segments.map(s => s.level), 0), 
    [segments]
  );

  // 检查是否所有片段都已提交
  const allSubmitted = segments.every(seg => submittedSegments.has(seg.id));

  // 获取完整句子文本
  const fullSentence = useMemo(() => {
    return tokens
      .filter(t => t.pos !== 'SPACE')
      .map(t => t.text)
      .join(' ')
      .replace(/ ([,.])/g, '$1');
  }, [tokens]);

  const handleTranslationChange = (id: string, value: string) => {
    setTranslations(prev => {
      const newMap = new Map(prev);
      newMap.set(id, value);
      return newMap;
    });
  };

  // 提交单个片段的翻译（只标记为已完成，不评分）
  const handleSubmitSegment = useCallback((segmentId: string) => {
    playClick();
    const userTranslation = translations.get(segmentId) || '';
    if (!userTranslation.trim()) {
      setErrors(prev => new Map(prev).set(segmentId, '请先输入翻译'));
      return;
    }

    setErrors(prev => {
      const newMap = new Map(prev);
      newMap.delete(segmentId);
      return newMap;
    });

    setSubmittedSegments(prev => new Set(prev).add(segmentId));

    // 自动聚焦到下一个未完成的输入框
    const currentIndex = segments.findIndex(s => s.id === segmentId);
    if (currentIndex !== -1) {
      // 寻找下一个未提交的片段
      for (let i = currentIndex + 1; i < segments.length; i++) {
        const nextSegment = segments[i];
        if (!submittedSegments.has(nextSegment.id) && nextSegment.id !== segmentId) {
          const nextInput = inputRefs.current.get(nextSegment.id);
          if (nextInput) {
            // 使用 setTimeout 确保状态更新后再聚焦
            setTimeout(() => nextInput.focus(), 50);
          }
          break;
        }
      }
    }
  }, [playClick, translations, segments, submittedSegments]);
  
  // 处理输入框键盘事件
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, segmentId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitSegment(segmentId);
    }
  }, [handleSubmitSegment]);

  // 统一评估（所有片段翻译完成后调用）
  const handleEvaluateAll = async () => {
    if (!aiConfigured) {
      onComplete(1);
      return;
    }

    setIsEvaluating(true);
    setGlobalError(null);

    try {
      // 按层级分组收集翻译
      const levelMap = new Map<number, { texts: string[]; translations: string[] }>();
      
      segments.forEach(seg => {
        const existing = levelMap.get(seg.level) || { texts: [], translations: [] };
        existing.texts.push(seg.text);
        existing.translations.push(translations.get(seg.id) || '');
        levelMap.set(seg.level, existing);
      });

      // 转换为 API 需要的格式
      const layersData = Array.from(levelMap.entries())
        .sort(([a], [b]) => a - b)
        .map(([level, data]) => ({
          currentLevelText: data.texts.join(' ').replace(/ ([,.])/g, '$1'),
          userTranslation: data.translations.join(''),
          level,
        }));

      const result = await evaluateAllTranslations(fullSentence, layersData);
      setEvaluation(result);
      // 评估完成后只显示结果，不自动跳转
      // 用户点击"完成练习"按钮后才会调用 onComplete
    } catch (error) {
      setGlobalError(error instanceof Error ? error.message : 'AI 评估失败');
    } finally {
      setIsEvaluating(false);
    }
  };

  // 获取某层的评分结果
  const getLayerScore = (level: number): LayerScore | undefined => {
    return evaluation?.layerScores.find(ls => ls.level === level);
  };

  // 完成练习
  const handleFinish = () => {
    const score = evaluation ? evaluation.overall.score / 100 : 1;
    // 构建完整的 AI 评价
    const aiFeedback = evaluation ? [
      evaluation.overall.feedback,
      evaluation.overall.reference ? `参考翻译：${evaluation.overall.reference}` : '',
      evaluation.overall.improvements.length > 0 
        ? `改进建议：${evaluation.overall.improvements.join('；')}` 
        : ''
    ].filter(Boolean).join('\n') : undefined;
    onComplete(score, aiFeedback);
  };

  // 层级颜色（与 Step4Clause 一致）
  const getLevelColor = (level: number) => {
    const colors = ['cyan', 'purple', 'yellow', 'pink', 'orange', 'green'];
    return colors[level % colors.length];
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div>
      {/* 进度显示 */}
      <div className="mb-4 flex items-center gap-3">
        <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-pink-500 to-purple-500"
            initial={{ width: 0 }}
            animate={{ width: `${(submittedSegments.size / segments.length) * 100}%` }}
          />
        </div>
        <span className="text-sm text-gray-500">
          {submittedSegments.size} / {segments.length}
        </span>
        {evaluation && (
          <span className={`text-sm font-bold ${getScoreColor(evaluation.overall.score)}`}>
            综合评分 {evaluation.overall.score}分
          </span>
        )}
      </div>

      {/* 翻译容器 - 带刻度线 */}
      <div className="rounded-lg bg-cyber-darker/50 border border-pink-500/10 mb-6 overflow-hidden max-h-[500px] overflow-y-auto">
        
        {/* 顶部层级刻度线 - 固定浮动 */}
        <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur border-b border-gray-700/50">
          <div className="flex h-8">
            {Array.from({ length: maxLevel + 1 }, (_, i) => {
              const color = getLevelColor(i);
              return (
                <div 
                  key={i}
                  className="flex items-center justify-center border-r border-gray-700/30 flex-shrink-0"
                  style={{ width: `${LEVEL_WIDTH}px` }}
                >
                  <span className={`
                    text-xs font-mono
                    ${color === 'cyan' ? 'text-cyan-400' : ''}
                    ${color === 'purple' ? 'text-purple-400' : ''}
                    ${color === 'yellow' ? 'text-yellow-400' : ''}
                    ${color === 'pink' ? 'text-pink-400' : ''}
                    ${color === 'orange' ? 'text-orange-400' : ''}
                    ${color === 'green' ? 'text-green-400' : ''}
                  `}>
                    L{i}
                  </span>
                </div>
              );
            })}
            {/* 剩余空间 - 内容区标识 */}
            <div className="flex-1 flex items-center px-3">
              <span className="text-xs text-gray-600">小句内容</span>
            </div>
          </div>
        </div>

        {/* 小句列表区域 - 带垂直虚线 */}
        <div className="relative py-4">
          {/* 垂直虚线层 - 颜色与刻度一致 */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: maxLevel + 1 }, (_, i) => {
              const color = getLevelColor(i);
              return (
                <div 
                  key={i}
                  className={`
                    absolute top-0 bottom-0 border-l border-dashed
                    ${color === 'cyan' ? 'border-cyan-500/30' : ''}
                    ${color === 'purple' ? 'border-purple-500/30' : ''}
                    ${color === 'yellow' ? 'border-yellow-500/30' : ''}
                    ${color === 'pink' ? 'border-pink-500/30' : ''}
                    ${color === 'orange' ? 'border-orange-500/30' : ''}
                    ${color === 'green' ? 'border-green-500/30' : ''}
                  `}
                  style={{ left: `${i * LEVEL_WIDTH + LEVEL_WIDTH / 2}px` }}
                />
              );
            })}
            {/* 最后一条边界虚线 */}
            <div 
              className="absolute top-0 bottom-0 border-l border-dashed border-gray-700/30"
              style={{ left: `${(maxLevel + 1) * LEVEL_WIDTH}px` }}
            />
          </div>

          {/* 小句片段 */}
          <div className="relative space-y-3 px-2">
            {segments.map((segment) => {
              const color = getLevelColor(segment.level);
              const isSubmitted = submittedSegments.has(segment.id);
              const layerScore = getLayerScore(segment.level);
              const error = errors.get(segment.id);
              
              return (
                <motion.div
                  key={segment.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`
                    rounded-lg transition-all duration-200
                    ${isSubmitted 
                      ? 'bg-green-500/10 border border-green-500/30' 
                      : 'bg-gray-800/60 border border-gray-700/50'}
                  `}
                  style={{ 
                    marginLeft: `${segment.level * LEVEL_WIDTH}px`,
                  }}
                >
                  {/* 小句内容行 */}
                  <div className="flex items-center gap-2 px-3 py-2">
                    {/* 层级小圆点 */}
                    <div className={`
                      w-2 h-2 rounded-full flex-shrink-0
                      ${color === 'cyan' ? 'bg-cyan-400' : ''}
                      ${color === 'purple' ? 'bg-purple-400' : ''}
                      ${color === 'yellow' ? 'bg-yellow-400' : ''}
                      ${color === 'pink' ? 'bg-pink-400' : ''}
                      ${color === 'orange' ? 'bg-orange-400' : ''}
                      ${color === 'green' ? 'bg-green-400' : ''}
                    `} />
                    
                    {/* 小句文本 */}
                    <span className="font-mono text-sm text-gray-200 flex-1">
                      {segment.tokens.map((token, idx) => {
                        const isProcess = showProcessHint && isProcessToken(token, tokens);
                        const isParticipant = showParticipantHint && isParticipantToken(token, tokens);
                        return (
                          <span
                            key={token.id}
                            className={`
                              ${isProcess ? 'text-red-300 font-semibold' : ''}
                              ${isParticipant ? 'text-blue-300 font-semibold' : ''}
                            `}
                          >
                            {token.text}
                            {idx < segment.tokens.length - 1 && token.pos !== 'PUNCT' && ' '}
                          </span>
                        );
                      })}
                    </span>

                    {/* 从句类型提示 */}
                    {segment.clauseInfo && (
                      <div className="relative flex-shrink-0">
                        <button
                          onClick={() => setShowClauseInfo(showClauseInfo === segment.id ? null : segment.id)}
                          className={`
                            p-1 rounded-full transition-all duration-200
                            ${showClauseInfo === segment.id 
                              ? 'bg-blue-500/30 text-blue-300' 
                              : 'hover:bg-gray-700/50 text-gray-400 hover:text-gray-300'}
                          `}
                          title="点击查看从句类型"
                        >
                          <Info className="w-3 h-3" />
                        </button>
                        
                        <AnimatePresence>
                          {showClauseInfo === segment.id && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.9, y: -5 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.9, y: -5 }}
                              className="absolute right-0 top-8 z-50 w-64 p-3 rounded-lg bg-gray-800 border border-blue-500/30 shadow-xl"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <span className="text-blue-400 font-semibold text-sm">
                                  {segment.clauseInfo.type}
                                </span>
                                <button
                                  onClick={() => setShowClauseInfo(null)}
                                  className="text-gray-500 hover:text-gray-300"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                              <p className="text-gray-300 text-sm">
                                {segment.clauseInfo.description}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}

                    {isSubmitted && <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />}
                    
                    {layerScore && (
                      <span className={`text-xs font-bold flex-shrink-0 ${getScoreColor(layerScore.score)}`}>
                        {layerScore.score}分
                      </span>
                    )}
                  </div>

                  {/* 翻译输入行 */}
                  <div className="flex gap-2 px-3 pb-2">
                    <input
                      ref={(el) => {
                        if (el) {
                          inputRefs.current.set(segment.id, el);
                        } else {
                          inputRefs.current.delete(segment.id);
                        }
                      }}
                      type="text"
                      value={translations.get(segment.id) || ''}
                      onChange={(e) => handleTranslationChange(segment.id, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, segment.id)}
                      placeholder="输入翻译... (回车确认)"
                      disabled={isSubmitted}
                      className={`cyber-input text-sm flex-1 py-1.5 ${isSubmitted ? 'opacity-60' : ''}`}
                    />
                    {!isSubmitted && (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleSubmitSegment(segment.id)}
                        disabled={!translations.get(segment.id)?.trim()}
                        className="cyber-button-primary px-2 py-1.5 disabled:opacity-50"
                        title="提交翻译 (或按回车)"
                      >
                        <Send className="w-3 h-3" />
                      </motion.button>
                    )}
                  </div>

                  {/* 错误提示 */}
                  {error && (
                    <div className="px-3 pb-2 text-sm text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {error}
                    </div>
                  )}

                  {/* 该层评估反馈（评估完成后显示） */}
                  {layerScore && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="px-3 pb-2"
                    >
                      <div className="p-2 rounded bg-gray-800/50 border border-gray-700/50">
                        <p className="text-xs text-gray-300">{layerScore.feedback}</p>
                        {layerScore.reference && (
                          <p className="text-xs text-cyan-400 mt-1">
                            参考: {layerScore.reference}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 统一评估按钮 - 始终显示，未完成时禁用 */}
      {!evaluation && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-6 p-4 rounded-lg border ${
            allSubmitted 
              ? 'bg-purple-500/10 border-purple-500/30' 
              : 'bg-gray-800/30 border-gray-700/30'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-display ${allSubmitted ? 'text-purple-300' : 'text-gray-400'}`}>
                {allSubmitted ? '所有片段翻译完成！' : `翻译进度：${submittedSegments.size} / ${segments.length}`}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {allSubmitted 
                  ? '点击"AI评分"获取每个小句评分和综合评估' 
                  : '请确认每个小句的翻译后进行AI评分'}
              </p>
            </div>
            <motion.button
              whileHover={allSubmitted ? { scale: 1.02 } : {}}
              whileTap={allSubmitted ? { scale: 0.98 } : {}}
              onClick={handleEvaluateAll}
              disabled={!allSubmitted || isEvaluating}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-display transition-all ${
                allSubmitted 
                  ? 'cyber-button-primary' 
                  : 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isEvaluating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>评估中...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>AI评分</span>
                </>
              )}
            </motion.button>
          </div>
          {globalError && (
            <div className="mt-3 text-sm text-red-400 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {globalError}
            </div>
          )}
        </motion.div>
      )}

      {/* 综合评估结果 */}
      {evaluation && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 space-y-4"
        >
          {/* 分层评分汇总 - 仅在有分层评分时显示 */}
          {evaluation.layerScores.length > 0 && (
            <div className="p-4 rounded-lg bg-gray-800/30 border border-gray-700/50">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span className="text-purple-300 font-display text-sm">各层评分</span>
              </div>
              <div className="flex flex-wrap gap-3">
                {evaluation.layerScores.map((ls) => {
                  const color = getLevelColor(ls.level);
                  return (
                    <div 
                      key={ls.level}
                      className={`
                        px-3 py-2 rounded-lg border
                        ${color === 'cyan' ? 'bg-cyan-500/10 border-cyan-500/30' : ''}
                        ${color === 'purple' ? 'bg-purple-500/10 border-purple-500/30' : ''}
                        ${color === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/30' : ''}
                        ${color === 'pink' ? 'bg-pink-500/10 border-pink-500/30' : ''}
                        ${color === 'orange' ? 'bg-orange-500/10 border-orange-500/30' : ''}
                        ${color === 'green' ? 'bg-green-500/10 border-green-500/30' : ''}
                      `}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`
                          text-xs font-mono
                          ${color === 'cyan' ? 'text-cyan-400' : ''}
                          ${color === 'purple' ? 'text-purple-400' : ''}
                          ${color === 'yellow' ? 'text-yellow-400' : ''}
                          ${color === 'pink' ? 'text-pink-400' : ''}
                          ${color === 'orange' ? 'text-orange-400' : ''}
                          ${color === 'green' ? 'text-green-400' : ''}
                        `}>
                          L{ls.level}
                        </span>
                        <span className={`text-lg font-bold ${getScoreColor(ls.score)}`}>
                          {ls.score}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 综合评分和反馈 */}
          <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <span className="text-purple-300 font-display">综合评分</span>
              <span className={`ml-auto text-2xl font-bold ${getScoreColor(evaluation.overall.score)}`}>
                {evaluation.overall.score}分
              </span>
            </div>
            <p className="text-gray-300">{evaluation.overall.feedback}</p>
          </div>

          {/* 完整参考翻译 */}
          {evaluation.overall.reference && (
            <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
              <div className="text-sm text-cyan-400 mb-2 font-semibold">完整参考翻译：</div>
              <p className="text-cyan-200">{evaluation.overall.reference}</p>
            </div>
          )}

          {/* 改进建议 */}
          {evaluation.overall.improvements.length > 0 && (
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <div className="text-sm text-yellow-400 mb-2 font-semibold">改进建议：</div>
              <ul className="text-yellow-200 space-y-2">
                {evaluation.overall.improvements.map((imp, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-yellow-400">•</span>
                    <span>{imp}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 完成按钮 */}
          <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
            <CheckCircle className="w-6 h-6 text-green-400" />
            <div className="flex-1">
              <span className="text-green-300 font-display">翻译练习完成！</span>
              <p className="text-sm text-gray-400 mt-1">
                已完成全部翻译评估，点击右侧按钮查看练习综合成绩。
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleFinish}
              className="cyber-button-primary"
            >
              查看综合评价
            </motion.button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
